import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { CButton, CAlert, CSpinner, CCard, CCardBody, CCardHeader } from '@coreui/react';
import { PositionsTable, type PositionRow } from '@trading/positions-ui';
import { getConfig } from '@/services/config.js';
import { closePosition } from '@/services/alpaca.js';
import { composePositionRows } from '@/services/positions-adapter.js';

const REFRESH_INTERVAL_MS = 60_000;

export const PositionsView = (): ReactElement => {
    const [rows, setRows] = useState<PositionRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [closingSymbol, setClosingSymbol] = useState<string | null>(null);
    const [pendingCloseSymbols, setPendingCloseSymbols] = useState<Set<string>>(new Set());
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const load = useCallback(async (): Promise<void> => {
        const cfg = getConfig();
        if (cfg == null) {
            setError('Position Manager is not configured. Go to Settings.');
            return;
        }

        setLoading(true);
        try {
            const result = await composePositionRows(cfg);
            setRows(result.rows);
            setError(result.errors.length > 0 ? result.errors.join('; ') : null);
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
        intervalRef.current = setInterval(() => void load(), REFRESH_INTERVAL_MS);
        return () => {
            if (intervalRef.current != null) {
                clearInterval(intervalRef.current);
            }
        };
    }, [load]);

    const handleClose = useCallback(
        async (row: PositionRow): Promise<void> => {
            const cfg = getConfig();
            if (cfg == null) {
                return;
            }

            setClosingSymbol(row.symbol);
            try {
                await closePosition(cfg, row.symbol);
                setPendingCloseSymbols((prev) => new Set(prev).add(row.symbol));
                await load();
            } catch (err) {
                setError((err as Error).message);
            } finally {
                setClosingSymbol(null);
            }
        },
        [load],
    );

    return (
        <CCard>
            <CCardHeader className="d-flex justify-content-between align-items-center">
                <span>Positions</span>
                <CButton color="secondary" variant="outline" size="sm" onClick={() => void load()}>
                    {loading ? <CSpinner size="sm" /> : '↻ Refresh'}
                </CButton>
            </CCardHeader>
            <CCardBody>
                {error && <CAlert color="danger">{error}</CAlert>}
                <PositionsTable
                    rows={rows}
                    atrLoading={loading}
                    closingSymbol={closingSymbol}
                    pendingCloseSymbols={pendingCloseSymbols}
                    onClose={(row) => void handleClose(row)}
                />
            </CCardBody>
        </CCard>
    );
};
